import { useGetOrders } from '@vindr/services/actions/order.action';
import { ORDER_STATUS } from '@vindr/services/constants/order.constant';
import { MATCH_STATUS } from '@vindr/services/constants/study.constant';
import { PaginationProps } from '@vindr/services/interfaces';
import { ParamsGetOrders, ResponseGetOrders } from '@vindr/services/src/apis/order.api';
import onPaginationChange from '@vindr/utils/state/onPaginationChange';
import { Button } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useImmer } from 'use-immer';
import Table, { VindrTableProps } from '../../ui/Table';
import Filter from './Filter';
import useOrderSearchColumns from './useOrderSearchColumns';

const OrderSearch = ({
  selectedOrders,
  onSave,
  onCancel,
  initFilters,
  message,
}: {
  selectedOrders: ResponseGetOrders['data'];
  onSave: (value: ResponseGetOrders['data']) => void;
  onCancel: Parameters<typeof Button>['0']['onClick'];
  initFilters: ParamsGetOrders;
  message: MessageInstance;
}) => {
  const columns = useOrderSearchColumns();
  const [params, setParams] = useState(initFilters);
  const { t } = useTranslation('Vindoc');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [pagination, setPagination] = useImmer<PaginationProps>({
    page: 1,
    size: 100,
  });
  const { data, isFetching, isLoading, refetch } = useGetOrders({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...params,
    match_status: MATCH_STATUS.Unmatched, //? With no study_uid
    status: [
      ORDER_STATUS.APPROVED,
      ORDER_STATUS.ARRIVED,
      ORDER_STATUS.NEW,
      ORDER_STATUS.READING,
      ORDER_STATUS.SUBMITTED,
    ],
  });
  const count = data?.count;
  const orderList = data?.data;

  const [isRefetch, setIsRefetch] = useState(false);
  useEffect(() => {
    if (!isFetching) {
      // After fetching, set isRefetch to false;
      setIsRefetch(false);
    }
  }, [isFetching]);

  const forceSearch = (params) => {
    setParams(params);
    // Set refetch to true only when force search
    setIsRefetch(true);
    refetch?.();
  };

  useEffect(() => {
    setSelectedRowKeys(selectedOrders.map((it) => it?.id));
  }, [selectedOrders]);

  return (
    <div className="flex flex-col">
      <Filter
        initFilters={initFilters}
        onChange={(p) => {
          setParams(p);
        }}
        forceSearch={forceSearch}
      />
      <Table
        wrapperClassName="h-auto"
        rowSelection={{
          selectedRowKeys,
          onChange: (keys, records) => {
            if (
              records &&
              ((selectedOrders?.length &&
                !records.every(
                  (it) =>
                    it?.visited_patient?.visit_id == selectedOrders[0]?.visited_patient.visit_id
                )) ||
                !records.every(
                  (it) => it?.visited_patient?.visit_id == records[0]?.visited_patient.visit_id
                ))
            ) {
              message.error(t('Visit ID mismatch'));

              return;
            }
            setSelectedRowKeys(keys);
          },
        }}
        columns={columns}
        loading={isLoading || isRefetch}
        dataSource={(orderList as any) || []}
        scroll={{ y: 500 }}
        rowKey="id"
        count={count}
        pagination={count < pagination.size ? null : pagination}
        onPaginationChange={(p: VindrTableProps['pagination']) => {
          setPagination((draft) => onPaginationChange(draft, p));
        }}
      />
      <div className="footer mt-3 flex justify-end">
        <Button onClick={onCancel}>{t('Cancel')}</Button>
        <Button
          type="primary"
          onClick={() => onSave(orderList.filter((it) => selectedRowKeys.includes(it.id)))}
          className="ml-2"
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default OrderSearch;
